import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Rectangle,
  ImageOverlay,
  CircleMarker,
  Polygon,
  Tooltip,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { AppStateShape } from '../../state/appState';
import type { LngLat, RadarPoint } from '../../types';
import { resolveUrl } from '../../api/client';
import { fmtCmYr } from '../../utils/format';
import './MapView.css';

type BasemapMode = 'DARK' | 'SATELLITE' | 'THREE_D';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
const mapboxTile = (path: string) => `https://api.mapbox.com/${path}?access_token=${MAPBOX_TOKEN}`;

function OverlayFilter({
  contrast,
  saturation,
}: {
  contrast: number;
  saturation: number;
}) {
  const map = useMap();
  useEffect(() => {
    const pane = map.getPanes().overlayPane;
    const filter = `contrast(${contrast}%) saturate(${saturation}%)`;
    pane.querySelectorAll<HTMLImageElement>('.technical-raster-overlay').forEach((img) => {
      img.style.filter = filter;
    });
  }, [map, contrast, saturation]);
  return null;
}

function bboxToLeaflet(b: [number, number, number, number]): L.LatLngBoundsExpression {
  const [minLon, minLat, maxLon, maxLat] = b;
  return [[minLat, minLon], [maxLat, maxLon]];
}

/** Velocity to color (matches backend cmap endpoints for visual consistency). */
function pointColor(v: number, highlighted = false): string {
  if (highlighted) return '#ff8a1c';
  const t = Math.max(-1, Math.min(1, v / 5));
  if (t < -0.05) {
    const f = Math.abs(t);
    return mix('#ffc56a', '#ff5a1f', f);
  }
  if (t > 0.05) {
    return mix('#ffd67d', '#ff8a1c', Math.abs(t));
  }
  return '#ffb34d';
}

function mix(a: string, b: string, t: number): string {
  const pa = hex(a), pb = hex(b);
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * t);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * t);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}

function hex(s: string): [number, number, number] {
  const v = s.replace('#', '');
  const n = v.length === 3
    ? v.split('').map((c) => parseInt(c + c, 16))
    : [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
  return [n[0], n[1], n[2]];
}

interface Props {
  state: AppStateShape;
}

export function MapView({ state }: Props) {
  const [basemap, setBasemap] = useState<BasemapMode>('SATELLITE');
  const {
    filters, layer, layerVisible, layerOpacity,
    layerContrast, layerSaturation,
    radarPoints, radarPointsVisible, setSelectedPointId, selectedPointId,
    drawMode, setDrawMode, drawnPolygon, setDrawnPolygon,
    highlightedPointIds,
  } = state;

  const center = useMemo<[number, number]>(() => {
    const [minLon, minLat, maxLon, maxLat] = filters.bbox;
    return [(minLat + maxLat) / 2, (minLon + maxLon) / 2];
  }, [filters.bbox]);

  const overlay = layer.kind === 'NONE' ? null :
    layer.kind === 'RGB' ? layer.data.overlay :
    layer.kind === 'INDEX' ? layer.data.overlay :
    layer.data.overlay;

  /** Fit map to AOI ∪ raster so HyP3 extent is not clipped when KML bbox is slightly smaller. */
  const mapFitBBox = useMemo(() => {
    if (!overlay || !layerVisible) return filters.bbox;
    const o = overlay.bbox;
    const f = filters.bbox;
    return [
      Math.min(f[0], o[0]),
      Math.min(f[1], o[1]),
      Math.max(f[2], o[2]),
      Math.max(f[3], o[3]),
    ] as [number, number, number, number];
  }, [filters.bbox, overlay, layerVisible]);

  const modeBadge = layer.kind === 'NONE' ? 'SIN CAPA' :
    layer.kind === 'RGB' ? layer.data.mode :
    layer.kind === 'INDEX' ? layer.data.mode :
    layer.data.mode;

  const refDate = layer.kind === 'NONE' ? '-' :
    layer.kind === 'RGB' ? layer.data.reference_date :
    layer.kind === 'INDEX' ? layer.data.reference_date :
    layer.kind === 'DISPLACEMENT' ? layer.data.reference_date :
    layer.kind === 'INTERFEROGRAM' ? layer.data.master_date : '-';

  const showPoints = radarPointsVisible && layer.kind === 'DISPLACEMENT' && radarPoints.length > 0;
  const showVelocityLegend = layer.kind === 'DISPLACEMENT';
  const showInterferogramLegend = layer.kind === 'INTERFEROGRAM';

  return (
    <div className={`map-wrap ${drawMode === 'DRAWING' ? 'map-drawing' : ''}`}>
      <MapContainer
        center={center}
        zoom={12}
        className={`map-root ${basemap === 'THREE_D' ? 'map-root-muted' : ''}`}
        zoomControl={false}
        attributionControl={true}
        doubleClickZoom={false}
      >
        {basemap === 'SATELLITE' ? (
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Tiles &copy; Esri"
            maxZoom={19}
          />
        ) : (
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://carto.com/">CARTO</a> | &copy; OpenStreetMap'
            subdomains="abcd"
            maxZoom={19}
          />
        )}

        <AOIRectangle bbox={filters.bbox} />

        {overlay && layerVisible && (
          <ImageOverlay
            key={overlay.id + ':' + layerOpacity}
            url={resolveUrl(overlay.url)}
            bounds={bboxToLeaflet(overlay.bbox)}
            opacity={layerOpacity}
            zIndex={400}
            className="technical-raster-overlay"
          />
        )}

        {overlay && layerVisible && layer.kind === 'DISPLACEMENT' && (
          <OverlayFilter
            contrast={layerContrast}
            saturation={layerSaturation}
          />
        )}

        {showPoints && (
          <RadarPointsLayer
            points={radarPoints}
            selectedId={selectedPointId}
            highlightedIds={highlightedPointIds}
            onSelect={setSelectedPointId}
          />
        )}

        {drawnPolygon && drawnPolygon.length >= 3 && (
          <DrawnPolygon ring={drawnPolygon} />
        )}

        {drawMode === 'DRAWING' && (
          <PolygonDrawer
            onComplete={(ring) => {
              setDrawnPolygon(ring);
              setDrawMode('DONE');
            }}
          />
        )}

        <FitToBBox bbox={mapFitBBox} />
      </MapContainer>

      {basemap === 'THREE_D' && (
        <ThreeDMapScene
          bbox={mapFitBBox}
          overlayBbox={overlay?.bbox ?? null}
          overlayUrl={overlay && layerVisible ? resolveUrl(overlay.url) : null}
          overlayOpacity={layerVisible ? layerOpacity : 0}
          polygon={drawnPolygon}
          points={showPoints ? radarPoints : []}
          selectedId={selectedPointId}
          highlightedIds={highlightedPointIds}
        />
      )}

      {/* Map badges */}
      <div className="map-badge">
        <span className="badge accent">{modeBadge}</span>
        <span className="badge">Fecha: {refDate}</span>
        {drawMode === 'DRAWING' && (
          <span className="badge warn">Dibujando poligono: clic para agregar vertices, doble clic o Finalizar para cerrar</span>
        )}
        {drawMode === 'DONE' && drawnPolygon && (
          <span className="badge success">Poligono seleccionado ({drawnPolygon.length} vertices)</span>
        )}
      </div>

      {/* Draw toolbar */}
      <MapDrawToolbar state={state} />
      <BasemapToggle basemap={basemap} onChange={setBasemap} />

      {showVelocityLegend && (
        <VelocityLegend
          threshold={state.colorThresholdCmYr}
          colorMax={state.colorMaxCmYr}
          startDate={filters.startDate}
          endDate={refDate !== '-' ? refDate : filters.endDate}
        />
      )}
      {showInterferogramLegend && <InterferogramLegend />}
    </div>
  );
}

function BasemapToggle({
  basemap,
  onChange,
}: {
  basemap: BasemapMode;
  onChange: (b: BasemapMode) => void;
}) {
  return (
    <div className="basemap-toggle" aria-label="Basemap">
      <button
        className={basemap === 'SATELLITE' ? 'active' : ''}
        onClick={() => onChange('SATELLITE')}
        title="Mapa base satelital"
      >
        SAT
      </button>
      <button
        className={basemap === 'THREE_D' ? 'active' : ''}
        onClick={() => onChange('THREE_D')}
        title="Visualizacion 3D del area"
      >
        3D
      </button>
      <button
        className={basemap === 'DARK' ? 'active' : ''}
        onClick={() => onChange('DARK')}
        title="Mapa base tecnico oscuro"
      >
        DRK
      </button>
    </div>
  );
}

function ThreeDMapScene({
  bbox,
  overlayBbox,
  overlayUrl,
  overlayOpacity,
  polygon,
  points,
  selectedId,
  highlightedIds,
}: {
  bbox: [number, number, number, number];
  overlayBbox: [number, number, number, number] | null;
  overlayUrl: string | null;
  overlayOpacity: number;
  polygon: LngLat[] | null;
  points: RadarPoint[];
  selectedId: string | null;
  highlightedIds: Set<string>;
}) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const hasMapbox = Boolean(MAPBOX_TOKEN);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const [minLon, minLat, maxLon, maxLat] = bbox;
    const center: [number, number] = [(minLon + maxLon) / 2, (minLat + maxLat) / 2];
    const map = new maplibregl.Map({
      container: mount,
      attributionControl: false,
      center,
      zoom: 12,
      pitch: 72,
      bearing: -28,
      maxPitch: 85,
      style: {
        version: 8,
        sources: {
          satellite: {
            type: 'raster',
            tiles: hasMapbox
              ? [mapboxTile('v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90')]
              : ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
            tileSize: hasMapbox ? 512 : 256,
            maxzoom: 19,
            attribution: hasMapbox ? 'Satellite imagery © Mapbox' : 'Tiles © Esri',
          },
          terrainDem: {
            type: 'raster-dem',
            tiles: hasMapbox
              ? [mapboxTile('v4/mapbox.terrain-rgb/{z}/{x}/{y}.pngraw')]
              : ['https://demotiles.maplibre.org/terrain-tiles/{z}/{x}/{y}.png'],
            tileSize: 256,
            encoding: hasMapbox ? 'mapbox' : 'terrarium',
            maxzoom: hasMapbox ? 14 : 12,
            attribution: hasMapbox ? 'Terrain © Mapbox' : 'Terrain © MapLibre demo tiles',
          },
        },
        layers: [
          { id: 'satellite', type: 'raster', source: 'satellite' },
          {
            id: 'terrain-shade',
            type: 'hillshade',
            source: 'terrainDem',
            paint: {
              'hillshade-shadow-color': '#07131d',
              'hillshade-highlight-color': '#ffffff',
              'hillshade-accent-color': '#1b6f83',
              'hillshade-exaggeration': 0.85,
            },
          },
        ],
        terrain: { source: 'terrainDem', exaggeration: 3 },
      },
    });

    map.addControl(new maplibregl.NavigationControl({
      showCompass: true,
      showZoom: false,
      visualizePitch: true,
    }), 'top-left');
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');

    map.once('load', () => {
      map.fitBounds([[minLon, minLat], [maxLon, maxLat]], {
        padding: 80,
        pitch: 72,
        bearing: -28,
        duration: 0,
      });

      if (overlayUrl && overlayBbox && overlayOpacity > 0) {
        const [oMinLon, oMinLat, oMaxLon, oMaxLat] = overlayBbox;
        map.addSource('displacement-overlay', {
          type: 'image',
          url: overlayUrl,
          coordinates: [
            [oMinLon, oMaxLat],
            [oMaxLon, oMaxLat],
            [oMaxLon, oMinLat],
            [oMinLon, oMinLat],
          ],
        });
        map.addLayer({
          id: 'displacement-overlay',
          type: 'raster',
          source: 'displacement-overlay',
          paint: {
            'raster-opacity': overlayOpacity,
            'raster-resampling': 'linear',
          },
        });
      }

      const aoiRing = polygon && polygon.length >= 3
        ? polygon
        : [
          [minLon, minLat],
          [maxLon, minLat],
          [maxLon, maxLat],
          [minLon, maxLat],
          [minLon, minLat],
        ];
      map.addSource('aoi-outline', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: { type: 'Polygon', coordinates: [aoiRing] },
        },
      });
      map.addLayer({
        id: 'aoi-fill',
        type: 'fill',
        source: 'aoi-outline',
        paint: { 'fill-color': '#f0e040', 'fill-opacity': 0.05 },
      });
      map.addLayer({
        id: 'aoi-line',
        type: 'line',
        source: 'aoi-outline',
        paint: { 'line-color': '#f0e040', 'line-width': 2 },
      });

      if (points.length > 0) {
        const stride = Math.max(1, Math.ceil(points.length / 2500));
        map.addSource('radar-points-3d', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: points.filter((_, i) => i % stride === 0).map((p) => ({
              type: 'Feature',
              properties: {
                velocity: p.mean_velocity_cm_yr,
                color: p.point_id === selectedId ? '#ffffff' : pointColor(p.mean_velocity_cm_yr, highlightedIds.has(p.point_id)),
                radius: p.point_id === selectedId ? 6 : highlightedIds.has(p.point_id) ? 4.6 : 3.4,
              },
              geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
            })),
          },
        });
        map.addLayer({
          id: 'radar-points-3d',
          type: 'circle',
          source: 'radar-points-3d',
          paint: {
            'circle-color': ['get', 'color'],
            'circle-radius': ['get', 'radius'],
            'circle-opacity': 0.85,
            'circle-stroke-color': '#07131d',
            'circle-stroke-width': 0.7,
          },
        });
      }
    });

    return () => map.remove();
  }, [bbox, hasMapbox, highlightedIds, overlayBbox, overlayOpacity, overlayUrl, points, polygon, selectedId]);

  return (
    <div className="maplibre-3d-map" ref={mountRef} />
  );
}

/** AOI rectangle with technical neon-cyan stroke. */
function AOIRectangle({ bbox }: { bbox: [number, number, number, number] }) {
  return (
    <Rectangle
      bounds={bboxToLeaflet(bbox)}
      pathOptions={{
        color: '#4cc2d4',
        weight: 1.5,
        opacity: 0.8,
        fillOpacity: 0.02,
        dashArray: '4 4',
      }}
    />
  );
}

function FitToBBox({ bbox }: { bbox: [number, number, number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(bboxToLeaflet(bbox), { padding: [40, 40] });
  }, [bbox, map]);
  return null;
}

interface RadarLayerProps {
  points: RadarPoint[];
  selectedId: string | null;
  highlightedIds: Set<string>;
  onSelect: (id: string) => void;
}

function RadarPointsLayer({ points, selectedId, highlightedIds, onSelect }: RadarLayerProps) {
  return (
    <>
      {points.map((p) => {
        const isSelected = p.point_id === selectedId;
        const isHighlighted = highlightedIds.has(p.point_id);
        const color = isSelected ? '#ffffff' : pointColor(p.mean_velocity_cm_yr, isHighlighted);
        return (
          <CircleMarker
            key={p.point_id}
            center={[p.lat, p.lon]}
            radius={isSelected ? 7 : isHighlighted ? 4.6 : 3.6}
            pathOptions={{
              color: isSelected ? '#ffffff' : isHighlighted ? '#ff7a18' : 'rgba(255, 103, 24, 0.72)',
              weight: isSelected ? 2 : isHighlighted ? 1.2 : 0.45,
              fillColor: color,
              fillOpacity: isHighlighted ? 0.95 : 0.62,
            }}
            eventHandlers={{
              click: () => onSelect(p.point_id),
            }}
          >
            <Tooltip direction="top" offset={[0, -6]} opacity={0.95} sticky>
              <div className="ps-tooltip">
                <div className="ps-tooltip-id">{p.point_id}</div>
                <div>
                  <span>v:</span>
                  <strong>{fmtCmYr(p.mean_velocity_cm_yr)}</strong>
                </div>
                <div>
                  <span>coh:</span>
                  <strong>{p.coherence.toFixed(2)}</strong>
                </div>
                {isHighlighted && <div className="ps-in-polygon">en polígono</div>}
              </div>
            </Tooltip>
          </CircleMarker>
        );
      })}
    </>
  );
}

/** Visual overlay for the drawn polygon. */
function DrawnPolygon({ ring }: { ring: LngLat[] }) {
  const positions = ring.map(([lon, lat]) => [lat, lon] as [number, number]);
  return (
    <Polygon
      positions={positions}
      pathOptions={{
        color: '#f0e040',
        weight: 2,
        opacity: 0.9,
        fillColor: '#f0e040',
        fillOpacity: 0.08,
        dashArray: '6 4',
      }}
    />
  );
}

/** Click-to-draw polygon capture logic. */
function PolygonDrawer({ onComplete }: { onComplete: (ring: LngLat[]) => void }) {
  const [draft, setDraft] = useState<LngLat[]>([]);
  const verticesRef = useRef<LngLat[]>([]);

  useEffect(() => {
    const finish = () => {
      const verts = verticesRef.current;
      if (verts.length >= 3) {
        onComplete([...verts, verts[0]]);
        verticesRef.current = [];
        setDraft([]);
      }
    };
    window.addEventListener('finish-polygon-drawing', finish);
    return () => window.removeEventListener('finish-polygon-drawing', finish);
  }, [onComplete]);

  useMapEvents({
    click(e) {
      const pt: LngLat = [e.latlng.lng, e.latlng.lat];
      verticesRef.current = [...verticesRef.current, pt];
      setDraft(verticesRef.current);
    },
    dblclick(e) {
      e.originalEvent.preventDefault();
      const verts = verticesRef.current;
      if (verts.length >= 3) {
        const closed = [...verts, verts[0]];
        onComplete(closed);
      }
      verticesRef.current = [];
      setDraft([]);
    },
  });

  if (draft.length === 0) return null;
  const positions = draft.map(([lon, lat]) => [lat, lon] as [number, number]);
  return (
    <>
      <Polygon
        positions={positions}
        pathOptions={{
          color: '#f0e040',
          weight: 2,
          fillOpacity: draft.length >= 3 ? 0.05 : 0,
          dashArray: '5 6',
        }}
      />
      {positions.map((pos, index) => (
        <CircleMarker
          key={`${pos[0]}-${pos[1]}-${index}`}
          center={pos}
          radius={4}
          pathOptions={{ color: '#111827', fillColor: '#f0e040', fillOpacity: 1, weight: 1 }}
        />
      ))}
    </>
  );
}

/** Floating draw toolbar on the map. */
function MapDrawToolbar({ state }: { state: AppStateShape }) {
  const { drawMode, setDrawMode, setDrawnPolygon, setPolygonResult, setHighlightedPointIds } = state;

  const handleStartDraw = useCallback(() => {
    setDrawnPolygon(null);
    setPolygonResult(null);
    setHighlightedPointIds(new Set());
    setDrawMode('DRAWING');
  }, [setDrawMode, setDrawnPolygon, setPolygonResult, setHighlightedPointIds]);

  const handleClear = useCallback(() => {
    setDrawnPolygon(null);
    setPolygonResult(null);
    setHighlightedPointIds(new Set());
    setDrawMode('NONE');
  }, [setDrawMode, setDrawnPolygon, setPolygonResult, setHighlightedPointIds]);

  return (
    <div className="map-draw-toolbar">
      {drawMode === 'NONE' && (
        <button
          id="btn-draw-polygon"
          className="draw-btn"
          onClick={handleStartDraw}
          title="Dibujar poligono de analisis"
        >
          <span className="draw-btn-icon">◆</span>
          Dibujar poligono
        </button>
      )}
      {drawMode === 'DRAWING' && (
        <button
          className="draw-btn draw-btn-cancel"
          onClick={handleClear}
        >
          Cancelar
        </button>
      )}
      {drawMode === 'DONE' && (
        <button
          className="draw-btn draw-btn-clear"
          onClick={handleClear}
        >
          Limpiar poligono
        </button>
      )}
    </div>
  );
}

function VelocityLegend({
  threshold,
  colorMax,
  startDate,
  endDate,
}: {
  threshold: number;
  colorMax: number;
  startDate: string;
  endDate: string;
}) {
  const middle = colorMax / 2;
  return (
    <div className="map-legend map-legend-rate">
      <div className="legend-header">
        <span>Leyenda</span>
        <span className="legend-unit">cm/yr</span>
      </div>
      <div className="legend-rate-title">Tasa de desplazamiento absoluto</div>
      <div className="legend-rate-body">
        <div className="legend-scale">
          <div className="legend-gradient legend-gradient-rate" />
        </div>
        <div className="legend-rate-labels">
          <span className="legend-label-high">&gt; {colorMax.toFixed(2)}</span>
          <span className="legend-label-mid">{middle.toFixed(2)}</span>
          <span>0</span>
        </div>
      </div>
      <div className="legend-threshold">Umbral: {threshold.toFixed(2)} cm/yr</div>
      <div className="legend-date-range">
        Del {formatLegendDate(startDate)} al {formatLegendDate(endDate)}
      </div>
    </div>
  );
}

function formatLegendDate(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function InterferogramLegend() {
  return (
    <div className="map-legend">
      <div className="legend-title">Fase envuelta</div>
      <div className="legend-gradient legend-gradient-ifg" />
      <div className="legend-ticks">
        <span>-pi</span>
        <span>0</span>
        <span>+pi</span>
      </div>
      <div className="legend-annotations">
        <span>1 cycle = ~2.8 cm</span>
      </div>
    </div>
  );
}
